<template>
  <div class="enhanced-input-container">
    <!-- File Attachment Preview -->
    <div v-if="attachedFile" class="attachment-preview">
      <div class="attachment-content">
        <div v-if="isImageFile(attachedFile)" class="attachment-image">
          <img :src="attachmentPreview" alt="Preview" />
        </div>
        <div v-else class="attachment-file">
          <v-icon size="32" color="#6B7FD7">{{ getFileIcon(attachedFile.type) }}</v-icon>
        </div>
        <div class="attachment-info">
          <div class="attachment-name">{{ attachedFile.name }}</div>
          <div class="attachment-size">{{ formatFileSize(attachedFile.size) }}</div>
        </div>
      </div>
      <button 
        class="attachment-remove" 
        @click="removeAttachment"
        :aria-label="'حذف فایل ضمیمه'"
        @keydown.enter="removeAttachment"
        @keydown.space.prevent="removeAttachment"
      >
        <v-icon size="18" color="#F44336">mdi-close</v-icon>
      </button>
    </div>

    <!-- Typing Indicator -->
    <div v-if="supportTyping" class="typing-indicator">
      <div class="typing-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <span class="typing-text">پشتیبانی در حال تایپ است...</span>
    </div>

    <!-- Input Area -->
    <div class="input-wrapper">
      <!-- Attach (right in RTL) -->
      <div class="input-actions-left">
        <button
          type="button"
          class="action-btn attach-btn"
          @click="triggerFileInput"
          :title="$t('dashboard.chat.attach') || 'ضمیمه فایل'"
          :aria-label="$t('dashboard.chat.attach') || 'ضمیمه فایل'"
          @keydown.enter="triggerFileInput"
          @keydown.space.prevent="triggerFileInput"
        >
          <v-icon size="20" class="attach-btn-icon">mdi-paperclip</v-icon>
        </button>
<!--        <button-->
<!--          class="action-btn emoji-btn"-->
<!--          @click="toggleEmojiPicker"-->
<!--          :title="$t('dashboard.chat.emoji') || 'ایموجی'"-->
<!--          :aria-label="$t('dashboard.chat.emoji') || 'ایموجی'"-->
<!--          :aria-expanded="showEmojiPicker"-->
<!--          @keydown.enter="toggleEmojiPicker"-->
<!--          @keydown.space.prevent="toggleEmojiPicker"-->
<!--        >-->
<!--          <v-icon size="20" color="#6B7FD7">mdi-emoticon-happy-outline</v-icon>-->
<!--        </button>-->
      </div>

      <!-- Text Input Area -->
      <div class="input-field-container">
        <textarea
          ref="textareaRef"
          v-model="messageText"
          @input="handleInput"
          @keydown="handleKeyDown"
          @focus="handleFocus"
          @blur="handleBlur"
          class="message-textarea"
          dir="rtl"
          :placeholder="placeholderText"
          :maxlength="maxLength"
          rows="1"
          :aria-label="placeholderText"
          aria-describedby="input-hint"
        ></textarea>
        <div class="input-footer">
<!--          <span v-if="showCharCounter" class="char-counter" :class="{ 'char-counter-warning': charCount > softLimit }">-->
<!--            {{ charCount }} / {{ maxLength }}-->
<!--          </span>-->
<!--          <span id="input-hint" class="input-hint">Enter برای ارسال، Shift+Enter برای خط جدید</span>-->
        </div>
      </div>

      <!-- Send (left in RTL) -->
      <div class="input-actions-right">
        <button
          type="button"
          class="action-btn send-btn"
          :class="{ 'send-btn-active': canSend, 'send-btn--sending': isSending }"
          @click="handleSend"
          :disabled="!canSend || isSending"
          :title="$t('dashboard.chat.send') || 'ارسال'"
          :aria-label="isSending ? 'در حال ارسال...' : ($t('dashboard.chat.send') || 'ارسال پیام')"
          :aria-busy="isSending"
          @keydown.enter="handleSend"
          @keydown.space.prevent="handleSend"
        >
          <v-icon v-if="!isSending" size="20" class="send-btn-icon">mdi-send</v-icon>
          <v-progress-circular v-else indeterminate size="18" width="2" color="white" aria-hidden="true"></v-progress-circular>
        </button>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      ref="fileInputRef"
      type="file"
      style="display: none"
      @change="handleFileSelect"
      :accept="acceptedFileTypes"
    />

    <!-- Emoji Picker Modal -->
    <v-dialog v-model="showEmojiPicker" max-width="400" content-class="emoji-picker-dialog">
      <v-card dir="rtl">
        <v-card-title class="emoji-picker-header">
          <span>انتخاب ایموجی</span>
          <button class="emoji-close-btn" @click="showEmojiPicker = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="emoji-picker-content">
          <div class="emoji-grid">
            <button
              v-for="emoji in commonEmojis"
              :key="emoji"
              class="emoji-item"
              @click="insertEmoji(emoji)"
            >
              {{ emoji }}
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'EnhancedMessageInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    isSending: {
      type: Boolean,
      default: false
    },
    supportTyping: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'چیزی بنویسید'
    }
  },
  data() {
    return {
      messageText: this.value || '',
      attachedFile: null,
      attachmentPreview: null,
      showEmojiPicker: false,
      showCharCounter: false,
      maxLength: 4000, // API allows up to 4000 characters for message field
      softLimit: 1800,
      minRows: 1,
      maxRows: 5,
      commonEmojis: [
        '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
        '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
        '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
        '🤗', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏',
        '😒', '🙄', '😬', '🤥', '😔', '😪', '😴', '😷',
        '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😵',
        '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏',
        '🙏', '💪', '❤️', '💛', '💚', '💙', '💜', '🖤',
        '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗',
        '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉',
        '☯️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌'
      ],
      acceptedFileTypes: 'image/*,.pdf,.doc,.docx,.txt',
      draftKey: 'chat_draft_message'
    }
  },
  computed: {
    canSend() {
      return (this.messageText && this.messageText.trim().length > 0) || this.attachedFile;
    },
    charCount() {
      return this.messageText.length;
    },
    placeholderText() {
      return this.placeholder || 'چیزی بنویسید';
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.messageText) {
        this.messageText = newVal;
      }
    },
    messageText(newVal) {
      this.$emit('input', newVal);
      this.autoSaveDraft();
      this.adjustTextareaHeight();
    }
  },
  mounted() {
    this.loadDraft();
    this.setupTypingIndicator();
  },
  beforeDestroy() {
    this.clearTypingInterval();
  },
  methods: {
    handleInput(event) {
      this.messageText = event.target.value;
      this.showCharCounter = this.charCount > 0;
    },
    handleKeyDown(event) {
      // Enter to send, Shift+Enter for new line
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (this.canSend) {
          this.handleSend();
        }
      }
    },
    handleFocus() {
      this.showCharCounter = this.charCount > 0;
    },
    handleBlur() {
      // Keep char counter visible if there's text
      if (this.charCount === 0) {
        this.showCharCounter = false;
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textareaRef;
      if (!textarea) return;

      textarea.style.height = 'auto';
      const lineHeight = 24;
      const minHeight = lineHeight * this.minRows;
      const maxHeightPx = 100;
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeightPx);
      textarea.style.height = newHeight + 'px';
      textarea.style.overflowY = scrollHeight > maxHeightPx ? 'auto' : 'hidden';
    },
    handleSend() {
      if (!this.canSend || this.isSending) return;

      const messageData = {
        text: this.messageText.trim(),
        file: this.attachedFile
      };

      this.$emit('send', messageData);
      
      // Clear input after sending
      this.messageText = '';
      this.removeAttachment();
      this.clearDraft();
      
      // Reset textarea height
      this.$nextTick(() => {
        const textarea = this.$refs.textareaRef;
        if (textarea) {
          textarea.style.height = 'auto';
        }
      });
    },
    triggerFileInput() {
      this.$refs.fileInputRef?.click();
    },
    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$emit('error', `حجم فایل (${fileSizeMB} مگابایت) بیشتر از حد مجاز (10 مگابایت) است`);
        // Reset file input
        event.target.value = '';
        return;
      }

      // Compress image files
      if (file.type.startsWith('image/')) {
        try {
          const ImageOptimizer = await import('@/utils/imageOptimizer');
          this.attachedFile = await ImageOptimizer.default.compressImage(file, {
            maxWidth: 1920,
            maxHeight: 1080,
            quality: 0.8
          });
        } catch (error) {
          console.error('Image compression failed:', error);
          this.attachedFile = file; // Use original if compression fails
        }
      } else {
        this.attachedFile = file;
      }

      // Create preview for images
      if (this.isImageFile(file)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.attachmentPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }

      // Reset file input
      event.target.value = '';
    },
    removeAttachment() {
      this.attachedFile = null;
      this.attachmentPreview = null;
    },
    isImageFile(file) {
      return file.type.startsWith('image/');
    },
    getFileIcon(fileType) {
      if (fileType.includes('pdf')) return 'mdi-file-pdf-box';
      if (fileType.includes('word') || fileType.includes('document')) return 'mdi-file-word-box';
      if (fileType.includes('text')) return 'mdi-file-document-outline';
      return 'mdi-file-outline';
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    insertEmoji(emoji) {
      const textarea = this.$refs.textareaRef;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = this.messageText;
      
      this.messageText = text.substring(0, start) + emoji + text.substring(end);
      
      // Set cursor position after emoji
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      });

      this.showEmojiPicker = false;
    },
    autoSaveDraft() {
      try {
        if (this.messageText.trim()) {
          localStorage.setItem(this.draftKey, this.messageText);
        } else {
          localStorage.removeItem(this.draftKey);
        }
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    },
    loadDraft() {
      try {
        const draft = localStorage.getItem(this.draftKey);
        if (draft) {
          this.messageText = draft;
          this.$nextTick(() => {
            this.adjustTextareaHeight();
          });
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey);
      } catch (error) {
        console.error('Error clearing draft:', error);
      }
    },
    setupTypingIndicator() {
      // This would typically be set up via WebSocket or polling
      // For now, it's controlled by the parent component via prop
    },
    clearTypingInterval() {
      // Clear any intervals if needed
    }
  }
}
</script>

<style scoped>
.enhanced-input-container {
  background: #FFFFFF;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0 0 4px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

/* Attachment Preview */
.attachment-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #FAFBFC;
  border-bottom: 1px solid #E0E0E0;
  direction: rtl;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.attachment-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.attachment-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #E0E0E0;
}

.attachment-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-file {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F5F5F5;
  border-radius: 8px;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 14px;
  font-weight: 500;
  color: #1A1A1A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.attachment-size {
  font-size: 12px;
  font-weight: 400;
  color: #666666;
}

.attachment-remove {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.attachment-remove:hover {
  background: #FFEBEE;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #FAFAFA;
  border-bottom: 1px solid #E0E0E0;
  direction: rtl;
  font-size: 12px;
  font-weight: 400;
  color: #666666;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9E9E9E;
  animation: typingDot 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typingDot {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

.typing-text {
  font-size: 12px;
  color: #757575;
}

/* Composer: rounded container (~20px), subtle border, lift shadow */
.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px;
  margin: 0 12px 12px;
  background: #FAFAFA;
  border: 1px solid #E0E0E0;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  direction: rtl;
}

.input-actions-left,
.input-actions-right {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.input-field-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.message-textarea {
  width: 100%;
  min-height: 40px;
  max-height: 100px;
  padding: 12px;
  border: 2px solid transparent;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  font-family: inherit;
  resize: none;
  overflow-y: auto;
  background: transparent;
  color: #1A1A1A;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  direction: rtl;
  text-align: right;
}

.message-textarea::placeholder {
  font-size: 14px;
  color: #787878;
}

.message-textarea:focus {
  outline: none;
  border-color: rgba(91, 124, 250, 0.5);
  box-shadow: 0 0 0 3px rgba(91, 124, 250, 0.2);
}

.message-textarea:focus-visible {
  outline: none;
  border-color: #5B7CFA;
  box-shadow: 0 0 0 3px rgba(91, 124, 250, 0.25);
}

.input-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
  font-size: 11px;
  color: #9E9E9E;
  direction: rtl;
}

.char-counter {
  font-size: 11px;
  color: #9E9E9E;
  font-weight: 500;
}

.char-counter-warning {
  color: #FF9800;
}

.input-hint {
  font-size: 10px;
  opacity: 0.7;
}

/* Buttons: 44px touch targets, 0.2s smooth hover/transition */
.action-btn {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  padding: 8px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;
}

.action-btn:focus {
  outline: none;
}

.action-btn:disabled {
  cursor: not-allowed;
}

/* Attach: right in RTL; clear hover feedback */
.attach-btn {
  background: transparent;
  transition: background 0.2s ease, transform 0.2s ease;
}

.attach-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
  transform: scale(1.05);
}

.attach-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.attach-btn-icon {
  color: #757575 !important;
  transition: color 0.2s ease;
}

.attach-btn:hover:not(:disabled) .attach-btn-icon {
  color: #424242 !important;
}

/* Send: blue when active; disabled = clearly gray (no reliance on opacity alone) */
.send-btn {
  background: #E0E0E0;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.send-btn .send-btn-icon {
  color: #9E9E9E !important;
  transform: rotate(180deg);
}

.send-btn:hover:not(:disabled) {
  background: #D5D5D5;
}

.send-btn:disabled {
  background: #E8E8E8;
  cursor: not-allowed;
}

.send-btn:disabled .send-btn-icon {
  color: #BDBDBD !important;
}

.send-btn-active {
  background: #5B7CFA;
}

.send-btn-active .send-btn-icon {
  color: #FFFFFF !important;
}

.send-btn-active:hover:not(:disabled) {
  background: #4A6BE8;
  box-shadow: 0 2px 8px rgba(91, 124, 250, 0.35);
  transform: scale(1.02);
}

.send-btn-active:active:not(:disabled) {
  transform: scale(0.98);
}

.send-btn--sending {
  opacity: 0.95;
  cursor: wait;
}

.send-btn--sending:disabled {
  cursor: wait;
}

/* Emoji Picker */
.emoji-picker-dialog {
  border-radius: 16px;
}

.emoji-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #E0E0E0;
  font-size: 16px;
  font-weight: 600;
  color: #424242;
}

.emoji-close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.emoji-close-btn:hover {
  background: #F5F5F5;
}

.emoji-picker-content {
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
}

.emoji-item {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.emoji-item:hover {
  background: #F0F4FF;
  transform: scale(1.2);
}

/* RTL Support */
[dir="ltr"] .input-wrapper {
  direction: ltr;
}

[dir="ltr"] .message-textarea {
  text-align: left;
}

/* Mobile Responsive */
@media (max-width: 767px) {
  .enhanced-input-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #FFFFFF;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
    z-index: 200;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .input-wrapper {
    padding: 8px;
    margin: 0 8px 10px;
  }

  .message-textarea {
    font-size: 16px;
    padding: 12px;
    min-height: 44px;
  }

  .action-btn {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .attachment-remove {
    min-width: 44px;
    min-height: 44px;
  }

  .attachment-preview {
    padding: 10px 12px;
  }

  .attachment-image,
  .attachment-file {
    width: 50px;
    height: 50px;
  }

  .attachment-remove {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .input-hint {
    display: none;
  }

  /* Virtual keyboard handling */
  .message-textarea:focus {
    /* Prevent layout shift when keyboard appears */
    scroll-margin-bottom: 200px;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .action-btn {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .attachment-preview,
  .typing-dots span,
  .action-btn {
    animation: none;
    transition: none;
  }
}

/* Focus: good contrast, no extra outline on textarea */
.action-btn:focus,
.message-textarea:focus,
.attachment-remove:focus {
  outline: none;
}

.action-btn:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
  transition: outline-offset 0.2s ease, box-shadow 0.2s ease;
}

.message-textarea:focus-visible {
  outline: none;
  border-color: #5B7CFA;
  box-shadow: 0 0 0 3px rgba(91, 124, 250, 0.25);
}

.attachment-remove:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .action-btn {
    border: 2px solid currentColor;
  }
  
  .message-textarea {
    border-width: 2px;
  }
  
  .send-btn-active {
    border: 2px solid #5B7CFA;
  }
}
</style>

